import type { DataViewFilter } from 'components/DataView';
import type { OrdersFilterInput } from 'data/__generated__';
import { anyStateOrganizationSource, totemSource } from 'data/entity-sources/sources';
import { DELIVERY_DAYS } from 'pages/Products/constants/weekdays';

import { ORDER_STATES_LABELS } from './states';
import type { OrderRow } from './orderBuckets';

const STATE_OPTIONS = Object.entries(ORDER_STATES_LABELS).map(([value, label]) => ({ value, label }));
const WEEKDAY_OPTIONS = DELIVERY_DAYS.map(({ name, label }) => ({ value: name, label }));

/**
 * Aucun `accessor` : tous ces filtres partent au SERVEUR, via `ordersFilter`. Les filtrer sur les lignes
 * déjà chargées n'aurait plus de sens depuis que la lecture est paginée — on ne filtrerait que la page
 * en cours, en laissant croire qu'il n'y a rien derrière.
 */
export const ORDER_FILTERS: DataViewFilter<OrderRow>[] = [
    {
        // Le périmètre de travail, allumé par défaut : les commandes en cours, plus celles archivées
        // dont la livraison est aujourd'hui — sans quoi la colonne « Archivées » du kanban reste vide.
        key: 'isCurrent',
        title: 'En cours',
        type: 'boolean',
        defaultValue: true,
    },
    {
        key: 'deliveryDate',
        title: 'Date de livraison',
        type: 'date',
    },
    {
        key: 'states',
        title: 'État',
        type: 'multi-select',
        options: STATE_OPTIONS,
    },
    {
        key: 'organizationIds',
        title: 'Organisation',
        type: 'async-multi-select',
        source: anyStateOrganizationSource,
    },
    {
        key: 'totemIds',
        title: 'Totem',
        type: 'async-multi-select',
        source: totemSource,
    },
    {
        key: 'weekdays',
        title: 'Jour de livraison',
        type: 'multi-select',
        options: WEEKDAY_OPTIONS,
    },
    {
        key: 'isPunctual',
        title: 'Ponctuelle',
        type: 'boolean',
    },
    {
        key: 'firstOrder',
        title: 'Première commande',
        type: 'boolean',
    },
];

/**
 * Une page large plutôt qu'une pagination fine : le kanban regroupe en colonnes, et il n'a pas de
 * défilement unique où accrocher un chargement continu. Au-delà, c'est son bouton qui prend le relais.
 */
export const ORDERS_PAGE_SIZE = 1000;

export const DEFAULT_ORDER_FILTERS: OrdersFilterInput = { isCurrent: true };

/**
 * L'état de filtre de la `DataView` → les variables de la query. Une clé absente ou vide n'est pas
 * envoyée : côté serveur, `null` et « tableau vide » ne veulent pas dire la même chose que « non filtré ».
 */
export function toOrdersFilterInput(filterState: Record<string, unknown>): OrdersFilterInput {
    const list = (value: unknown) => (Array.isArray(value) && value.length ? (value as string[]) : undefined);
    const bool = (value: unknown) => (typeof value === 'boolean' ? value : undefined);

    return {
        // `searchValue` est la clé sous laquelle la `DataView` range la recherche.
        searchValue: (filterState.searchValue as string)?.trim() || undefined,
        isCurrent: bool(filterState.isCurrent),
        states: list(filterState.states) as OrdersFilterInput['states'],
        organizationIds: list(filterState.organizationIds),
        totemIds: list(filterState.totemIds),
        weekdays: list(filterState.weekdays),
        isPunctual: bool(filterState.isPunctual),
        firstOrder: bool(filterState.firstOrder),
        deliveryDate: (filterState.deliveryDate as string) || undefined,
    };
}
